import React, { useEffect, useState } from "react";
import QRCode from "qrcode.react";
import { Row, Col } from "react-bootstrap";
import { invoice } from "../../../Types/Invoice";
import clientAxios from "../../../clients/clientAxios";
import "./invoice.css";

const programCache = new Map<string, { kind: string; label: string }>();

/**
 * @param classroomName Commission name stored on the bill
 * @returns Career label when the commission belongs to a career, course fallback otherwise
 */
const resolveProgramLabel = async (classroomName: string) => {
  const cachedProgram = programCache.get(classroomName);
  if (cachedProgram) {
    return cachedProgram;
  }
  const fallbackProgram = { kind: "course", label: "" };
  try {
    const classroomResponse = await clientAxios.get(
      `/classroom/name/${encodeURIComponent(classroomName)}`
    );
    const classroomPayload = classroomResponse?.data;
    const classroomData = Array.isArray(classroomPayload)
      ? classroomPayload.find(
          (candidate) => candidate?.classroom === classroomName
        ) || classroomPayload[0]
      : classroomPayload;
    const careerReference =
      classroomData?.carreer || classroomData?.career || classroomData?.carrer;
    const careerFromClassroom =
      typeof careerReference === "object" && careerReference !== null
        ? careerReference.name
        : "";
    if (careerFromClassroom) {
      const careerProgram = { kind: "career", label: careerFromClassroom };
      programCache.set(classroomName, careerProgram);
      return careerProgram;
    }
    if (typeof careerReference === "string" && careerReference.trim() !== "") {
      try {
        const careerResponse = await clientAxios.get(
          `/carreers/${careerReference}`
        );
        const careerName =
          careerResponse?.data?.name || careerResponse?.data?.carreer?.name || "";
        if (careerName) {
          const careerProgram = { kind: "career", label: careerName };
          programCache.set(classroomName, careerProgram);
          return careerProgram;
        }
      } catch (careerError) {
        const careerListResponse = await clientAxios.get("/carreers");
        const careerList = Array.isArray(careerListResponse?.data)
          ? careerListResponse.data
          : [];
        const matchedCareer = careerList.find(
          (career) => career?._id === careerReference
        );
        if (matchedCareer?.name) {
          const careerProgram = { kind: "career", label: matchedCareer.name };
          programCache.set(classroomName, careerProgram);
          return careerProgram;
        }
      }
    }
  } catch (classroomError) {
    return fallbackProgram;
  }
  return fallbackProgram;
};

const parseBillDate = (rawDate = "2020-01-30") => {
  const datePart = rawDate.slice(0, 10);
  const dateSegments = datePart.split("-");
  const billYear = Number(dateSegments[0]) || 2020;
  const billMonth = (Number(dateSegments[1]) || 1) - 1;
  const billDay = Number(dateSegments[2]) || 1;
  return new Date(billYear, billMonth, billDay);
};

export default function Invoice({
  data,
  isDuplicate = false,
}: {
  data: invoice;
  isDuplicate?: boolean;
}) {
  const verificationBaseUrl =
    "https://payments.rollingcodeschool.com/live/invoice/?bill_id=";
  const verificationUrl = `${verificationBaseUrl}${data._id || ""}`;
  const receiptDate = parseBillDate(data.date ? data.date : "2020-01-30");
  const formattedLongDate = Intl.DateTimeFormat("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(receiptDate);
  const formattedAmount = (Number(data.amount) || 0).toLocaleString("es-AR");
  const receiptNumber = data.billNumber ? `001-${data.billNumber}` : "S/N";
  const classroomLabel = data.classroom ? data.classroom : "Sin asignar";
  const installmentLabel = data.monthlyInstallment ? data.monthlyInstallment : "—";
  const fallbackCourseLabel = data.course ? data.course : "—";
  const [programLabel, setProgramLabel] = useState(fallbackCourseLabel);
  const [programKind, setProgramKind] = useState("course");

  useEffect(() => {
    let activeRequest = true;
    setProgramLabel(fallbackCourseLabel);
    setProgramKind("course");
    if (!data.classroom) {
      return;
    }
    resolveProgramLabel(data.classroom).then((resolvedProgram) => {
      if (!activeRequest) {
        return;
      }
      if (resolvedProgram.kind === "career" && resolvedProgram.label) {
        setProgramLabel(resolvedProgram.label);
        setProgramKind("career");
      }
    });
    return () => {
      activeRequest = false;
    };
  }, [data.classroom, fallbackCourseLabel]);
  const paymentLabel = data.payment ? data.payment : "—";
  const receiverName = data?.createdBy?.name || data?.nameDisplay || "—";
  const studentName = data.nameDisplay || "Alumno sin nombre";
  const trimmedNotes = (data.additionalNotes || "").trim();
  const hasNotes = trimmedNotes.length > 0;
  const fullPayment = data.fullPaid === true;
  const partialPayment = data.partial === true && !fullPayment;
  const amountContext = fullPayment
    ? `${paymentLabel} · Pago total`
    : `${paymentLabel} · Cuota ${installmentLabel}`;

  return (
    <article
      className={isDuplicate ? "rc-invoice rc-invoice-duplicate" : "rc-invoice"}
    >
      <div className="rc-invoice-accent" />
      <div className="rc-invoice-body">
        <header className="rc-invoice-header">
          <div className="rc-invoice-brand">
            <img
              src={process.env.PUBLIC_URL + "/rclogo.png"}
              alt="RollingCode School"
              className="rc-invoice-logo"
            />
            <div>
              <p className="rc-invoice-school">RollingCode School</p>
              <p className="rc-invoice-title">Comprobante interno de pago</p>
              <p className="rc-invoice-subtitle">No válido como factura</p>
            </div>
          </div>
          <div className="rc-invoice-meta">
            <p className="rc-invoice-number">Recibo {receiptNumber}</p>
            <p className="rc-invoice-date">{formattedLongDate}</p>
            <div className="rc-invoice-flags">
              <span
                className={
                  isDuplicate
                    ? "rc-invoice-flag rc-invoice-flag-dark"
                    : "rc-invoice-flag"
                }
              >
                {isDuplicate ? "Duplicado" : "Original"}
              </span>
              {data.prepay ? (
                <span className="rc-invoice-flag rc-invoice-flag-warm">Seña</span>
              ) : null}
              {fullPayment ? (
                <span className="rc-invoice-flag rc-invoice-flag-accent">
                  Pago total
                </span>
              ) : null}
              {partialPayment ? (
                <span className="rc-invoice-flag rc-invoice-flag-warm">
                  Pago parcial
                </span>
              ) : null}
            </div>
          </div>
        </header>

        <section>
          <p className="rc-invoice-section-label">Detalle del comprobante</p>
          <Row>
            <Col md={6}>
              <p className="rc-invoice-field">
                <span className="rc-invoice-field-label">Alumno</span>
                <span className="rc-invoice-field-value">{studentName}</span>
              </p>
            </Col>
            <Col md={6}>
              <p className="rc-invoice-field">
                <span className="rc-invoice-field-label">
                  {programKind === "career" ? "Carrera" : "Curso"}
                </span>
                <span className="rc-invoice-field-value">{programLabel}</span>
              </p>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <p className="rc-invoice-field">
                <span className="rc-invoice-field-label">Comisión</span>
                <span className="rc-invoice-field-value">{classroomLabel}</span>
              </p>
            </Col>
            <Col>
              <p className="rc-invoice-field">
                <span className="rc-invoice-field-label">Cuota</span>
                <span className="rc-invoice-field-value">{installmentLabel}</span>
              </p>
            </Col>
            <Col>
              <p className="rc-invoice-field">
                <span className="rc-invoice-field-label">Método de pago</span>
                <span className="rc-invoice-field-value">{paymentLabel}</span>
              </p>
            </Col>
          </Row>
        </section>

        <section className="rc-invoice-amount">
          <div>
            <span className="rc-invoice-amount-label">Importe total</span>
            <span className="rc-invoice-amount-note">{amountContext}</span>
          </div>
          <span className="rc-invoice-amount-value">$ {formattedAmount}</span>
        </section>

        {hasNotes ? (
          <section className="rc-invoice-notes">
            <span className="rc-invoice-field-label">Observaciones</span>
            <p className="rc-invoice-notes-text">{trimmedNotes}</p>
          </section>
        ) : null}

        <footer className="rc-invoice-footer">
          <Row className="align-items-center">
            <Col md={5}>
              <div className="rc-invoice-verify">
                <span className="rc-invoice-qr">
                  <QRCode value={verificationUrl} size={72} renderAs="svg" level="M" />
                </span>
                <p className="rc-invoice-verify-text">
                  Verificá este comprobante en payments.rollingcodeschool.com
                  <span className="rc-invoice-verify-id">{data._id || ""}</span>
                </p>
              </div>
            </Col>
            <Col md={7}>
              <p className="rc-invoice-sign">
                Recibo por:{" "}
                <span className="rc-invoice-sign-name">{receiverName}</span>
              </p>
              <div className="rc-invoice-sign-lines">
                <span className="rc-invoice-sign-line">Recibí conforme</span>
                <span className="rc-invoice-sign-line">Aclaración</span>
              </div>
            </Col>
          </Row>
        </footer>
      </div>
    </article>
  );
}
